import { Injectable, WritableSignal } from '@angular/core';

export enum DeepLinkOpeningStatus {
  idle = 'idle',
  opening = 'opening',
  success = 'success',
  error = 'error',
  blocked = 'blocked',
}

@Injectable({ providedIn: 'root' })
export class DeepLinkOpenerService {
  /**
   * Opens a deep link in a new tab and detects success/failure.
   */
  async openInNewTab(
    url: string,
    signal: WritableSignal<DeepLinkOpeningStatus>,
    timeoutMs = 1200
  ): Promise<void> {
    signal.set(DeepLinkOpeningStatus.opening);

    // Open intermediate tab
    const win = window.open('', '_blank');

    if (!win) {
      // Popup blocked
      signal.set(DeepLinkOpeningStatus.blocked);
      return Promise.resolve();
    }

    // Write something minimal to have access to the tab's DOM
    win.document.writeln(`
      <html>
        <body style="font-family:sans-serif;padding:20px;">
          <p>Abrindo app…</p>
        </body>
      </html>
    `);

    // Try to navigate to the deep link
    try {
      win.location.href = url;
      // Cannot put any code here as it will not be executed
    } catch (e) {
      // Some browsers may throw an error with custom schemes
      // This does not interrupt the timeout logic
    }

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        try {
          // Try to access the document
          // If still accessible → the app did NOT open → error
          if (win.document) {
            // Close the zombie tab
            win.close();

            signal.set(DeepLinkOpeningStatus.error);
            return resolve();
          }
          signal.set(DeepLinkOpeningStatus.success);
          return resolve();
        } catch (e) {
          // If accessing the document throws:
          // - The tab lost its context
          // - Probably the OS switched to the native app
          signal.set(DeepLinkOpeningStatus.success);
          resolve();
        }
      }, timeoutMs);
    });
  }

  async openInSameTab(
    url: string,
    signal: WritableSignal<DeepLinkOpeningStatus>,
    timeoutMs = 1200
  ): Promise<void> {
    signal.set(DeepLinkOpeningStatus.opening);

    // Try to navigate to the deep link
    try {
      window.location.href = url;
      // Cannot put any code here as it will not be executed
    } catch (e) {
      // Some browsers may throw an error with custom schemes
      // This does not interrupt the timeout logic
    }
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        // If we are still on the same page after the timeout → error
        signal.set(DeepLinkOpeningStatus.error);
        resolve();
      }, timeoutMs);
    });
  }
}
